import React from "react";
import { connect } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  OutlinedInput,
  InputLabel,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import { useHistory } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { onLogout, onLogin } from "../../../redux/session/actions";
import { authenticationService } from "../../services/AuthenticationService";
import MainContent from "../../components/main-content/MainContent";
import { GenericPortalError } from "../../components/error-display/PortalError";
import { mstrPath } from "../../../utils/mstrBase";

function LoginPage(props) {
  const { config, token, onLogin, messageBundles, onLogout } = props;
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [isLogingUser, setIsLogingUser] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const history = useHistory();
  const allowPasswordLogin = Number(config?.loginMode) === 1;

  const handleGenericAppError = useCallback((err) => {
    history.push(mstrPath(`error?type=${err.id}`));
  }, [history]);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const completeLogin = useCallback(async (mstrToken) => {
    if (!mstrToken) return false;

    let usergroups = [];

    try {
      usergroups = (await authenticationService.getUserGroups(config, mstrToken)) || [];
    } catch (_e) {
      usergroups = [];
    }

    onLogin(mstrToken, usergroups);

    return true;
  }, [config, onLogin]);

  const handleLoginSSO = useCallback(async () => {
    setError(null);
    setIsLogingUser(true);

    try {
      const token = await window.microstrategy.auth.samlLogin(
        config?.samlLoginURL || "https://techint.cloud.microstrategy.com/MicroStrategyLibrary",
      );

      if (await completeLogin(token)) {
        setIsLogingUser(false);
      } else {
        setIsLogingUser(false);
        throw new GenericPortalError("INVALID_CREDENTIALS", "error", null);
      }
    } catch (e) {
      if (e.id) {
        setError(e);
      } else {
        handleGenericAppError(new GenericPortalError("LIBRARY_UNAVAILABLE", "error", null));
      }
      setIsLogingUser(false);
    }
  }, [completeLogin, config, handleGenericAppError]);

  useEffect(() => {
    async function login() {
      if (!config) return;

      try {
        if (token !== null) {
          const renewedToken = await authenticationService.isSessionAlive(config, token);

          if (renewedToken != null) {
            await completeLogin(renewedToken);
          }
        } else {
          onLogout();
          await handleLoginSSO();
        }
      } catch (_e) {
        onLogout();
      } finally {
        setAutoLoginAttempted(true);
      }
    }

    login();
  }, [completeLogin, config, handleLoginSSO, onLogout, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setIsLogingUser(true);

    try {
      const data = new FormData(event.currentTarget);
      const mstrToken = await authenticationService.login(config, data.get("email"), data.get("password"));

      if (await completeLogin(mstrToken)) {
        setIsLogingUser(false);
        window.history.back();
      }
    } catch (e) {
      if (e.id) {
        setError(e);
      } else {
        handleGenericAppError(new GenericPortalError("LIBRARY_UNAVAILABLE", "error", null));
      }
      setIsLogingUser(false);
    }
  };

  if (token) {
    history.push(mstrPath());

    return null;
  } else {
    const autoLoginInProgress = !autoLoginAttempted || isLogingUser;
    const showCredentialsForm = false;

    return (
      <MainContent
        sx={{
          background:
            "linear-gradient(137deg, rgba(17, 81, 143, 1) 15%, rgba(25,118,210,1) 22%, rgba(16,152,226,1) 31%)",
        }}
      >
        <Box
          sx={{
            width: "50%",
            marginLeft: "25%",
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100%",
            backgroundColor: "white",
          }}
        >
          {allowPasswordLogin && showCredentialsForm && (
            <Box
              noValidate
              component="form"
              sx={{ mt: 1, margin: "30% 30% 0% 30%" }}
              onSubmit={handleSubmit}
            >
              <TextField
                autoFocus
                fullWidth
                required
                autoComplete="email"
                id="email"
                label={messageBundles.GENERIC_04}
                margin="normal"
                name="email"
                sx={{
                  fill: "white",
                }}
              />
              <FormControl sx={{ m: 1, width: "100%", margin: "0px" }} variant="outlined">
                <InputLabel htmlFor="outlined-adornment-password">Contrasena</InputLabel>
                <OutlinedInput
                  fullWidth
                  required
                  autoComplete="current-password"
                  endAdornment={
                    <InputAdornment position="">
                      <IconButton
                        aria-label="toggle password visibility"
                        edge="end"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  id="outlined-adornment-password"
                  label={messageBundles.GENERIC_05}
                  margin="normal"
                  name="password"
                  type={showPassword ? "text" : "password"}
                />
              </FormControl>
              {!isLogingUser && (
                <Button fullWidth sx={{ mt: 3, mb: 2 }} type="submit" variant="contained">
                  {messageBundles.GENERIC_06}
                </Button>
              )}
              {isLogingUser && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                    padding: "10px",
                  }}
                >
                  <CircularProgress
                    sx={{
                      color: "#1d5fa0",
                      width: "30px !important",
                      height: "30px !important",
                    }}
                  >
                    Loading
                  </CircularProgress>
                </Box>
              )}
            </Box>
          )}
          {!allowPasswordLogin && autoLoginInProgress && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-evenly",
                alignItems: "center",
                padding: "10px",
                marginTop: "24px",
              }}
            >
              <CircularProgress
                sx={{
                  color: "#1d5fa0",
                  width: "30px !important",
                  height: "30px !important",
                }}
              >
                Loading
              </CircularProgress>
              <Typography variant="body2">Iniciando sesion automaticamente...</Typography>
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ width: "40%", marginTop: "10px" }} variant="filled">
              {error.message}
            </Alert>
          )}
        </Box>
      </MainContent>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    token: state.userDetails?.token,
    messageBundles: state.configuration?.messageBundles,
    groups: state.userDetails?.groups,
    config: state.configuration,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      onLogin: onLogin,
      onLogout: onLogout,
    },
    dispatch,
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);
